import { Article } from '@wix/ambassador-answers-search-proxy/types';
import { Category } from '@wix/answers-api';
import Head from 'next/head';
import React, { FunctionComponent } from 'react';

import { LOCALES, SEO } from '../../../constants';
import useIsKbServiceEnabled from '../../../hooks/useIsKbServiceEnabled';
import { PageSeo } from '../../../types';
import { shouldAddNoIndex } from '../../../utils/should-add-no-index';
import { stripHTML } from '../../../utils/text';
import { BasicSeoElements } from '../BasicSEO';
import { useBabelSeo } from '../useBabelSeo';

type ArticlePageProps = {
  article?: Article;
  pageSeo: PageSeo | null;
  categoriesTree?: Category[];
};

export const ArticlePageSEO: FunctionComponent<ArticlePageProps> = ({
  article,
  pageSeo,
  categoriesTree,
}) => {
  const resolveBabelSeo = useBabelSeo();
  // The hidden-category rule relies on the kb-server tree, which prunes hidden
  // categories; only trust the tree membership check when that path is active.
  const isKbServiceEnabled = useIsKbServiceEnabled();
  const noIndexCategoriesTree = isKbServiceEnabled ? categoriesTree : undefined;
  const parseArticleDescription = (content?: string) => {
    if (!content) {
      return '';
    } else {
      const MAX_DESCRIPTION_LENGTH = 160;
      return stripHTML(content).trim().slice(0, MAX_DESCRIPTION_LENGTH);
    }
  };

  const description = pageSeo
    ? article &&
    pageSeo.description
      .replace(SEO.PLACEHOLDERS.ARTICLE_PREVIEW, parseArticleDescription(article.content))
      .replace(SEO.PLACEHOLDERS.ARTICLE_TITLE, article?.title || '')
      .replace(SEO.PLACEHOLDERS.COMPANY_NAME, SEO.COMPANY_NAME)
    : parseArticleDescription(article?.content);

  const fallbackTitle = pageSeo
    ? pageSeo.title
      .replace(SEO.PLACEHOLDERS.ARTICLE_TITLE, article?.title || '')
      .replace(SEO.PLACEHOLDERS.COMPANY_NAME, SEO.COMPANY_NAME)
    : undefined;

  const title = resolveBabelSeo('seo.article_page.title', fallbackTitle, {
    articleTitle: article?.title || '',
  });

  const alternateDefaultUrl = article?.translations?.find(
    (e) => e.locale === LOCALES.EN
  )?.url;

  const noIndex = shouldAddNoIndex(article, noIndexCategoriesTree);
  const robots = pageSeo
    ? (noIndex ? { ...pageSeo.robots, noIndex: true } : pageSeo.robots)
    : { noArchive: false, noFollow: false, noIndex: noIndex ?? false };

  return (
    <>
      <BasicSeoElements
        title={title}
        description={description}
        imageUrl={pageSeo?.imageUrl ?? SEO.SOCIAL_IMAGE_URL}
        robots={robots}
      />
      <Head>
        {article?.translations?.map(({ url, locale: localeTr }) => (
          <link rel="alternate" hrefLang={localeTr} href={url} key={url} />
        ))}
        {alternateDefaultUrl && (
          <link
            rel="alternate"
            hrefLang="x-default"
            href={alternateDefaultUrl}
          />
        )}
      </Head>
    </>
  );
};
